import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './components/ui/button';
import { X, Download, ZoomIn, ZoomOut, RotateCcw, Printer, Share2 } from 'lucide-react';

// Self-hosted pdf.js (placed under /public/pdfjs)
const PDFJS_BASE = '/pdfjs';

let pdfJsLoadPromise = null;
const loadPdfJs = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfJsLoadPromise) return pdfJsLoadPromise;
  pdfJsLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${PDFJS_BASE}/pdf.min.js`;
    script.async = true;
    script.onload = () => {
      try {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}/pdf.worker.min.js`;
        resolve(window.pdfjsLib);
      } catch (e) { reject(e); }
    };
    script.onerror = () => {
      // CDN fallback if self-hosted files missing
      const s2 = document.createElement('script');
      s2.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      s2.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        resolve(window.pdfjsLib);
      };
      s2.onerror = reject;
      document.head.appendChild(s2);
    };
    document.head.appendChild(script);
  });
  return pdfJsLoadPromise;
};

const MIN_SCALE = 0.25;
const MAX_SCALE = 5;
const DEFAULT_SCALE = 0.46;

const PDFViewerModal = ({ isOpen, onClose, pdfUrl, title, onDownload }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pages, setPages] = useState([]);
  const [renderProgress, setRenderProgress] = useState({ done: 0, total: 0 });
  const [scaleDisplay, setScaleDisplay] = useState(DEFAULT_SCALE);
  const [baseSize, setBaseSize] = useState({ width: 0, height: 0 });

  const viewportRef = useRef(null);
  const sizerRef = useRef(null);   // expands to take scaled space (drives scrollbars)
  const contentRef = useRef(null); // gets the scale() transform
  const scaleRef = useRef(DEFAULT_SCALE);
  const gestureRef = useRef(null);

  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  // Reset when opening
  useEffect(() => {
    if (!isOpen) return;
    scaleRef.current = DEFAULT_SCALE;
    setScaleDisplay(DEFAULT_SCALE);
  }, [isOpen]);

  // Load and render PDF (progressivo: 1ª página aparece na hora, resto vai chegando)
  useEffect(() => {
    if (!isOpen || !pdfUrl) return;
    let cancelled = false;
    let pdfDoc = null;
    setIsLoading(true);
    setError(null);
    setPages([]);
    setRenderProgress({ done: 0, total: 0 });

    (async () => {
      try {
        const pdfjsLib = await loadPdfJs();
        let source;
        if (pdfUrl.startsWith('data:')) {
          const base64 = pdfUrl.split(',')[1];
          const bin = atob(base64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          source = { data: bytes };
        } else {
          source = { url: pdfUrl, withCredentials: false };
        }

        const pdf = await pdfjsLib.getDocument(source).promise;
        pdfDoc = pdf;
        if (cancelled) return;

        // Resolução adaptativa: documentos grandes usam escala menor para não
        // estourar a memória do WebView no celular (crash = app "reinicia")
        const pageCount = pdf.numPages;
        const renderScale = pageCount > 10 ? 1.5 : pageCount > 5 ? 2 : 2.5;
        setRenderProgress({ done: 0, total: pageCount });

        // Um único canvas reutilizado em todas as páginas (economiza memória)
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        for (let i = 1; i <= pageCount; i++) {
          const page = await pdf.getPage(i);
          if (cancelled) return;
          const viewport = page.getViewport({ scale: renderScale });
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          await page.render({ canvasContext: ctx, viewport }).promise;
          if (cancelled) return;
          const pageData = {
            src: canvas.toDataURL('image/jpeg', 0.8),
            width: viewport.width / renderScale,
            height: viewport.height / renderScale,
          };
          try { page.cleanup(); } catch (_) { /* noop */ }
          setPages(prev => [...prev, pageData]);
          setRenderProgress({ done: i, total: pageCount });
          if (i === 1) setIsLoading(false); // mostra a 1ª página imediatamente
        }
        // Libera memória do canvas e do worker do pdf.js
        canvas.width = 0;
        canvas.height = 0;
        try { pdf.cleanup(); } catch (_) { /* noop */ }
      } catch (e) {
        console.error('PDF load error:', e);
        if (!cancelled) {
          setError('Erro ao carregar PDF: ' + (e?.message || e));
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      try { pdfDoc && pdfDoc.destroy(); } catch (_) { /* noop */ }
    };
  }, [isOpen, pdfUrl]);

  // Compute base size after pages render, fit to viewport width
  useEffect(() => {
    if (pages.length === 0 || !viewportRef.current) return;
    const vp = viewportRef.current;
    const vpWidth = vp.clientWidth - 32; // account for 16px padding x2
    const maxPageWidth = Math.max(...pages.map(p => p.width));
    // Pure fit-to-width: scale page so the widest page fits viewport width.
    // Cap at 1.5 so small PDFs aren't blown up; allow shrinking below 1 for wide PDFs.
    const fit = Math.min(1.5, vpWidth / maxPageWidth);
    const totalHeight = pages.reduce((sum, p) => sum + p.height * fit, 0) + (pages.length - 1) * 12 + 32;
    setBaseSize({ width: maxPageWidth * fit + 32, height: totalHeight });
  }, [pages]);

  // Apply scale transform + sizer dims
  const applyScale = useCallback((newScale, scrollAdjust) => {
    const s = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
    scaleRef.current = s;
    if (contentRef.current) {
      contentRef.current.style.transform = `scale(${s})`;
    }
    if (sizerRef.current && baseSize.width > 0) {
      sizerRef.current.style.width = `${baseSize.width * s}px`;
      sizerRef.current.style.height = `${baseSize.height * s}px`;
    }
    if (scrollAdjust && viewportRef.current) {
      viewportRef.current.scrollLeft = scrollAdjust.x;
      viewportRef.current.scrollTop = scrollAdjust.y;
    }
    setScaleDisplay(s);
  }, [baseSize]);

  // Re-apply when baseSize changes
  useEffect(() => {
    if (baseSize.width > 0) applyScale(scaleRef.current);
  }, [baseSize, applyScale]);

  // Zoom centered at a viewport point (vx, vy in viewport coords)
  const zoomAt = useCallback((newScale, vx, vy) => {
    const vp = viewportRef.current;
    const sizer = sizerRef.current;
    if (!vp) return;
    const clamped = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
    const cur = scaleRef.current;
    const sizerRect = sizer?.getBoundingClientRect();
    const vpRect = vp.getBoundingClientRect();
    const curOffsetX = sizerRect ? sizerRect.left - vpRect.left + vp.scrollLeft : 0;
    const curOffsetY = sizerRect ? sizerRect.top - vpRect.top + vp.scrollTop : 0;
    const contentX = (vx + vp.scrollLeft - curOffsetX) / cur;
    const contentY = (vy + vp.scrollTop - curOffsetY) / cur;
    const newSizerW = baseSize.width * clamped;
    const newOffsetX = newSizerW < vp.clientWidth ? (vp.clientWidth - newSizerW) / 2 : 0;
    const newOffsetY = 0;
    applyScale(clamped, {
      x: contentX * clamped + newOffsetX - vx,
      y: contentY * clamped + newOffsetY - vy,
    });
  }, [applyScale, baseSize]);

  // Touch gesture handlers (pinch-to-zoom + double-tap)
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp || pages.length === 0) return;

    let lastTap = 0;
    let lastTapPos = { x: 0, y: 0 };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const [a, b] = e.touches;
        const rect = vp.getBoundingClientRect();
        const sizerRect = sizerRef.current?.getBoundingClientRect();
        const dx = b.clientX - a.clientX;
        const dy = b.clientY - a.clientY;
        const dist = Math.hypot(dx, dy);
        const midX = (a.clientX + b.clientX) / 2 - rect.left;
        const midY = (a.clientY + b.clientY) / 2 - rect.top;
        // Offset of sizer's left/top within the scrollable area (accounts for auto-margin centering)
        const sizerOffsetX = sizerRect ? sizerRect.left - rect.left + vp.scrollLeft : 0;
        const sizerOffsetY = sizerRect ? sizerRect.top - rect.top + vp.scrollTop : 0;
        gestureRef.current = {
          startDist: dist,
          startScale: scaleRef.current,
          midX, midY,
          startScrollX: vp.scrollLeft,
          startScrollY: vp.scrollTop,
          sizerOffsetX, sizerOffsetY,
        };
      }
    };

    const onTouchMove = (e) => {
      if (e.touches.length === 2 && gestureRef.current) {
        e.preventDefault();
        const [a, b] = e.touches;
        const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
        const g = gestureRef.current;
        const ratio = dist / g.startDist;
        const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, g.startScale * ratio));
        // Keep pinch midpoint stable in viewport, accounting for sizer offset:
        //   contentPos_in_sizer (scaled) = (midX + scrollLeft) - sizerOffsetX
        //   contentPos_original = that / oldScale
        //   newPos_in_sizer = contentPos_original * newScale
        //   newScrollLeft = newPos_in_sizer + newSizerOffsetX - midX
        const contentX = (g.midX + g.startScrollX - g.sizerOffsetX) / g.startScale;
        const contentY = (g.midY + g.startScrollY - g.sizerOffsetY) / g.startScale;
        // Predict new sizer offset (auto-margin recenters if sizer is narrower than viewport)
        const newSizerW = baseSize.width * newScale;
        const newSizerH = baseSize.height * newScale;
        const newSizerOffsetX = newSizerW < vp.clientWidth ? (vp.clientWidth - newSizerW) / 2 : 0;
        const newSizerOffsetY = newSizerH < vp.clientHeight ? 0 : 0; // sizer always at top
        const newScrollX = contentX * newScale + newSizerOffsetX - g.midX;
        const newScrollY = contentY * newScale + newSizerOffsetY - g.midY;
        applyScale(newScale, { x: newScrollX, y: newScrollY });
      }
    };

    const onTouchEnd = (e) => {
      if (e.touches.length === 0) {
        gestureRef.current = null;
        // Double-tap detection (single-finger taps)
        if (e.changedTouches.length === 1) {
          const t = e.changedTouches[0];
          const now = Date.now();
          const dx = t.clientX - lastTapPos.x;
          const dy = t.clientY - lastTapPos.y;
          if (now - lastTap < 300 && Math.hypot(dx, dy) < 30) {
            // Double tap: toggle zoom
            const rect = vp.getBoundingClientRect();
            const mx = t.clientX - rect.left;
            const my = t.clientY - rect.top;
            const cur = scaleRef.current;
            if (cur > DEFAULT_SCALE * 1.1) {
              zoomAt(DEFAULT_SCALE, mx, my);
            } else {
              zoomAt(2.2, mx, my);
            }
            lastTap = 0;
          } else {
            lastTap = now;
            lastTapPos = { x: t.clientX, y: t.clientY };
          }
        }
      }
    };

    vp.addEventListener('touchstart', onTouchStart, { passive: false });
    vp.addEventListener('touchmove', onTouchMove, { passive: false });
    vp.addEventListener('touchend', onTouchEnd);
    vp.addEventListener('touchcancel', onTouchEnd);

    // Desktop: wheel + ctrl to zoom
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const rect = vp.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      zoomAt(scaleRef.current * factor, mx, my);
    };
    vp.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      vp.removeEventListener('touchstart', onTouchStart);
      vp.removeEventListener('touchmove', onTouchMove);
      vp.removeEventListener('touchend', onTouchEnd);
      vp.removeEventListener('touchcancel', onTouchEnd);
      vp.removeEventListener('wheel', onWheel);
    };
  }, [pages, applyScale, zoomAt, baseSize]);

  const zoomIn = () => {
    const vp = viewportRef.current;
    if (!vp) return;
    zoomAt(scaleRef.current * 1.3, vp.clientWidth / 2, vp.clientHeight / 2);
  };
  const zoomOut = () => {
    const vp = viewportRef.current;
    if (!vp) return;
    zoomAt(scaleRef.current / 1.3, vp.clientWidth / 2, vp.clientHeight / 2);
  };
  const resetZoom = () => {
    applyScale(DEFAULT_SCALE, { x: 0, y: 0 });
  };

  // Imprime usando as páginas já renderizadas (imagens) via window.print() do
  // documento principal. Isso abre o diálogo de impressão NATIVO em iOS WKWebView
  // e no desktop, sem baixar arquivo (o open de blob PDF no iOS baixa em vez de imprimir).
  const handlePrint = async () => {
    if (!pages || pages.length === 0) return;

    // Remove qualquer resíduo de impressão anterior
    const oldRoot = document.getElementById('pdf-print-root');
    if (oldRoot) oldRoot.remove();
    const oldStyle = document.getElementById('pdf-print-style');
    if (oldStyle) oldStyle.remove();

    const style = document.createElement('style');
    style.id = 'pdf-print-style';
    style.textContent = `
      #pdf-print-root { display: none; }
      @media print {
        html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
        body > *:not(#pdf-print-root) { display: none !important; }
        #pdf-print-root { display: block !important; }
        #pdf-print-root .pdf-print-page {
          text-align: center;
          page-break-after: always;
          break-after: page;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        #pdf-print-root .pdf-print-page:last-child {
          page-break-after: auto;
          break-after: auto;
        }
        /* Limita cada imagem a uma página inteira, mantendo a proporção.
           Assim nenhuma página transborda um fiapo para a próxima (páginas brancas no meio). */
        #pdf-print-root img {
          display: inline-block;
          max-width: 100%;
          max-height: 100vh;
          width: auto;
          height: auto;
        }
        @page { margin: 0; }
      }
    `;
    document.head.appendChild(style);

    const root = document.createElement('div');
    root.id = 'pdf-print-root';
    const imgs = pages.map((p) => {
      const wrap = document.createElement('div');
      wrap.className = 'pdf-print-page';
      const img = document.createElement('img');
      img.src = p.src;
      wrap.appendChild(img);
      root.appendChild(wrap);
      return img;
    });
    document.body.appendChild(root);

    // Garante que todas as imagens estejam carregadas antes de imprimir
    await Promise.all(
      imgs.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; })
      )
    );

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      root.remove();
      style.remove();
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);

    // Pequeno atraso para o layout de impressão assentar, então chama o diálogo nativo
    setTimeout(() => {
      try { window.focus(); window.print(); } catch (_) { /* noop */ }
      // iOS WKWebView nem sempre dispara 'afterprint' — limpeza de segurança
      setTimeout(cleanup, 60000);
    }, 150);
  };

  const handleDownload = async () => {
    if (onDownload) { onDownload(); return; }
    try {
      const res = await fetch(pdfUrl);
      const blob = await res.blob();
      const fileName = `${(title || 'document').replace(/[^a-zA-Z0-9\s\-_]/g, '')}.pdf`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      try { window.open(pdfUrl, '_blank'); } catch (_) { /* noop */ }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="pdf-viewer-modal"
      style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', flexDirection: 'column', background: '#1f2937' }}
    >
      <style>{`@keyframes pdfspin { to { transform: rotate(360deg); } }`}</style>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#fff', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
        <span style={{ fontSize: '14px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, marginRight: 8 }}>
          {title || 'Visualizar PDF'}
        </span>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
          {renderProgress.total > 0 && renderProgress.done < renderProgress.total && (
            <span data-testid="pdf-render-progress" style={{ fontSize: 10, color: '#6b7280', whiteSpace: 'nowrap' }}>
              {renderProgress.done}/{renderProgress.total}
            </span>
          )}
          <Button data-testid="pdf-zoom-out" variant="outline" size="sm" onClick={zoomOut} className="h-7 w-7 p-0" disabled={isLoading || !!error}>
            <ZoomOut className="h-3.5 w-3.5" />
          </Button>
          <span style={{ fontSize: 11, color: '#6b7280', minWidth: 36, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(scaleDisplay * 100)}%
          </span>
          <Button data-testid="pdf-zoom-in" variant="outline" size="sm" onClick={zoomIn} className="h-7 w-7 p-0" disabled={isLoading || !!error}>
            <ZoomIn className="h-3.5 w-3.5" />
          </Button>
          <Button data-testid="pdf-zoom-reset" variant="outline" size="sm" onClick={resetZoom} className="h-7 w-7 p-0" disabled={isLoading || !!error}>
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
          <Button data-testid="pdf-download" variant="outline" size="sm" onClick={handleDownload} title="Download" aria-label="Download" className="h-7 w-7 p-0 ml-1">
            <Download className="h-3.5 w-3.5" />
          </Button>
          <Button data-testid="pdf-print" variant="outline" size="sm" onClick={handlePrint} disabled={isLoading || !!error || pages.length === 0} title="Imprimir" aria-label="Imprimir" className="h-7 w-7 p-0 bg-blue-600 text-white hover:bg-blue-700 border-blue-600">
            <Printer className="h-3.5 w-3.5" />
          </Button>
          {typeof navigator !== 'undefined' && navigator.share && (
            <Button
              data-testid="pdf-share"
              variant="outline"
              size="sm"
              title="Compartilhar"
              aria-label="Compartilhar"
              className="h-7 w-7 p-0 bg-green-600 text-white hover:bg-green-700 border-green-600"
              onClick={async () => {
                try {
                  const res = await fetch(pdfUrl);
                  const blob = await res.blob();
                  const file = new File([blob], `${(title || 'document').replace(/[^a-zA-Z0-9\s\-_]/g, '')}.pdf`, { type: 'application/pdf' });
                  await navigator.share({ title: title || 'PDF', files: [file] });
                } catch (e) { console.warn('Share failed:', e); }
              }}
            >
              <Share2 className="h-3.5 w-3.5" />
            </Button>
          )}
          <button
            data-testid="pdf-close"
            onClick={onClose}
            aria-label="Fechar"
            style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4, border: 'none', background: 'transparent', cursor: 'pointer' }}
          >
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>
      </div>

      {/* Scrollable viewport */}
      <div
        ref={viewportRef}
        data-testid="pdf-viewport"
        style={{
          flex: 1,
          overflow: 'auto',
          background: '#374151',
          WebkitOverflowScrolling: 'touch',
          // Allow native 1-finger pan in both axes; we capture 2-finger ourselves
          touchAction: 'pan-x pan-y',
          position: 'relative',
        }}
      >
        {isLoading ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ display: 'inline-block', width: 40, height: 40, border: '3px solid #4b5563', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'pdfspin 1s linear infinite' }} />
              <p style={{ color: '#d1d5db', fontSize: 14, marginTop: 12 }}>Carregando PDF...</p>
            </div>
          </div>
        ) : error ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 24, maxWidth: 400, textAlign: 'center' }}>
              <p style={{ color: '#dc2626', fontWeight: 600, marginBottom: 12 }}>{error}</p>
              <Button onClick={onClose} variant="outline">Fechar</Button>
            </div>
          </div>
        ) : (
          // Sizer expands to scaled dimensions so native scrollbars work at any zoom
          <div
            ref={sizerRef}
            style={{
              width: baseSize.width ? `${baseSize.width}px` : '100%',
              height: baseSize.height ? `${baseSize.height}px` : 'auto',
              position: 'relative',
              margin: '0 auto',
            }}
          >
            <div
              ref={contentRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: baseSize.width ? `${baseSize.width}px` : '100%',
                transform: 'scale(1)',
                transformOrigin: '0 0',
                willChange: 'transform',
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                boxSizing: 'border-box',
              }}
            >
              {pages.map((p, idx) => {
                // Scale page to baseSize.width (minus padding) preserving aspect ratio
                const maxW = baseSize.width ? baseSize.width - 32 : p.width;
                const fit = Math.min(maxW / p.width, 2);
                return (
                  <img
                    key={idx}
                    src={p.src}
                    alt={`Página ${idx + 1}`}
                    draggable={false}
                    data-testid={`pdf-page-${idx + 1}`}
                    style={{
                      width: p.width * fit,
                      height: p.height * fit,
                      maxWidth: '100%',
                      background: '#fff',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
                      display: 'block',
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFViewerModal;
